import React, { useCallback, useEffect, useState } from "react";
import {
  Form,
  Button,
  Spinner,
  Row,
  Col,
  Badge,
  InputGroup,
} from "react-bootstrap";
import { FormItem, SVGIcon } from "../../components/Base";
import PageCenter from "../../components/PageCenter";
import BrandWebsite from "../../partials/BrandWebsite";
import TitleHelmet from "../../partials/TitleHelmet";
import ProSourceForm from "../../components/ProSourceForm";
import {
  getStorage,
  setStorage,
  removeStorage,
} from "../../../_metronic/_helpers";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import get from "lodash/get";
import { useParams, useLocation, Redirect } from "react-router-dom";
import core from "../../vendors/core-api";
import { confirmWalkInDetails } from "./queue/ConfirmWalkInDetails";
import EditWalkInDetailsModal, {
  UPDATED_MESSAGE,
  GONE_MESSAGE,
} from "./queue/EditWalkInDetailsModal";
import {
  validateWalkInDetails,
  MOBILE_ERROR,
  EMAIL_ERROR,
} from "../../helpers/walkInValidation";
import { isEmpty } from "lodash";

const minimalCardStyle = {
  width: "500px",
  background: "#fff",
  padding: "2.5rem 2rem",
  borderRadius: "12px",
  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.07)",
  border: "1px solid #f0f0f0",
};

const logoStyle = {
  height: "70px",
  width: "70px",
  objectFit: "cover",
  borderRadius: "50%",
  marginBottom: "1rem",
  border: "2px solid #f4f4f4",
};

const experiencePillStyle = {
  cursor: "pointer",
  transition: "all 0.2s ease",
  padding: "0.5rem 1rem",
  margin: "0.25rem",
  border: "1px solid #dee2e6",
};

const AnimationStyles = () => (
  <style>
    {`
      .card-animate-enter {
        animation: card-fade-in 0.5s ease-out forwards;
      }
      @keyframes card-fade-in {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }

      .content-pop-in {
        animation: content-pop-in 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
      }
      @keyframes content-pop-in {
        from { opacity: 0; transform: scale(0.8); }
        to { opacity: 1; transform: scale(1); }
      }

      .animated-queue-button {
        transition: transform 0.2s ease, box-shadow 0.2s ease;
      }
      .animated-queue-button:hover:not(:disabled) {
        transform: scale(1.03);
        box-shadow: 0 5px 15px rgba(0, 123, 255, 0.2);
      }
      .animated-queue-button:active:not(:disabled) {
        transform: scale(0.98);
        box-shadow: none;
      }

      .wave-container {
        display: flex;
        justify-content: center;
        align-items: flex-end;
        height: 20px;
      }
      .wave-dot {
        font-size: 3rem;
        line-height: 0.5;
        color: #aaa;
        animation: wave-bounce 1.4s infinite;
        margin: 0 2px;
      }
      @keyframes wave-bounce {
        0%, 60%, 100% { transform: translateY(0); }
        30% { transform: translateY(-8px); }
      }

      .number-wiggle {
        display: inline-block; 
        animation: wiggle 2.5s ease-in-out infinite;
      }

      @keyframes wiggle {
        0%, 100% { transform: rotate(0deg); }
        25% { transform: rotate(2deg); }
        75% { transform: rotate(-2deg); }
      }

      .cancel-button-animated {
        transition: transform 0.2s ease, color 0.2s ease;
      }
      .cancel-button-animated:hover:not(:disabled) {
        transform: translateY(-1px);
        color: #a71d2a !important;
      }

      body.swal2-shown > [aria-hidden="true"] {
        transition: none !important;
      }
      body.swal2-shown {
        padding-right: 0 !important;
        overflow: hidden;
      }
    `}
  </style>
);

export default function QueuePage(props) {
  const { store_url = "" } = useParams();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const listKey = query.get("listKey") || "";
  const redisKey = query.get("redisKey") || "";
  const qrToken = query.get("t") || "";
  const qrDate = query.get("d") || "";
  return (
    <MainComponent
      {...props}
      store_url={store_url}
      listKey={listKey}
      redisKey={redisKey}
      qrToken={qrToken}
      qrDate={qrDate}
    />
  );
}

class MainComponent extends BrandWebsite {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      view: "form",
      queueData: null,
      listKey: props.listKey || "",
      redisKey: props.redisKey || "",
      qrToken: props.qrToken || "",
      qrDate: props.qrDate || "",
      qrValidated: false,
    };
    this.handleCancelSuccess = this.handleCancelSuccess.bind(this);
  }

  componentDidMount() {
    super.componentDidMount();
    const { listKey, redisKey } = this.state;

    const hasQueueParams = listKey && redisKey;

    try {
      const queueData = getStorage("walkInQueue");
      if (queueData) {
        const data = JSON.parse(queueData);
        if (data.expiry && new Date().getTime() < data.expiry) {
          this.setState({ view: "position", queueData: data });
        } else {
          removeStorage("walkInQueue");
        }
      } else if (hasQueueParams) {
        // No localStorage entry but params are present (e.g. direct link to queue position)
        this.setState({ view: "position" });
      }
    } catch (e) {
      console.error("Failed to parse queueData:", e);
      removeStorage("walkInQueue");
    }
  }

  componentDidUpdate() {
    const { qrToken, qrDate, view, qrValidated } = this.state;

    // Trigger QR validation once brand data has loaded, when qrToken + qrDate are present
    // but only if there is no existing active walk-in session in storage
    if (
      view === "form" &&
      qrValidated === false &&
      !getStorage("walkInQueue")
    ) {
      const store = this.getActiveStore();
      if (!store) return;
      const { _id: storeId, queueing = {} } = store;

      const api = core("pu").get();

      if (
        queueing?.enabled &&
        !isEmpty(queueing?.validateLink) &&
        queueing?.validateLink !== "never"
      ) {
        this.setState({ view: "validating_qr" });

        if (!qrToken && !qrDate) {
          this.setState({ view: "invalid_qr", qrValidated: true });
          return;
        }

        api
          .get(
            `/branded-website/reservation/${storeId}/validate-qr-token?t=${encodeURIComponent(
              qrToken,
            )}&d=${encodeURIComponent(qrDate)}`,
          )
          .then(({ data }) => {
            if (data && data.valid) {
              this.setState({ view: "form", qrValidated: true });
            } else {
              this.setState({ view: "invalid_qr", qrValidated: true });
            }
          })
          .catch((err) => {
            console.log(err);
            this.setState({ view: "invalid_qr", qrValidated: true });
          });
      } else {
        this.setState({ qrValidated: true });
      }
    }
  }

  handleSuccessfulRegistration = (queueData) => {
    const expiry = new Date().getTime() + 24 * 60 * 60 * 1000;
    const dataToStore = { ...queueData, expiry };
    setStorage("walkInQueue", JSON.stringify(dataToStore), expiry);
    this.setState({ view: "position", queueData: dataToStore });
  };

  handleCancelSuccess = () => {
    removeStorage("walkInQueue");
    this.setState({ view: "form", queueData: null, qrValidated: false });
  };

  getActiveStore = () => {
    if (this.state.data) {
      const {
        data: { stores = [] },
      } = this.state;
      const store_url = this.props.store_url;

      let store = "";
      if (store_url !== "") {
        const filteredStore = stores.filter(
          (store) => store.store_url === store_url,
        );
        store = filteredStore[0];
      } else {
        store = stores[0];
      }

      return store;
    }
  };

  render() {
    const { data = {}, data_status, view, queueData } = this.state;
    const isLoading = data_status !== "fetched";
    const store = this.getActiveStore();

    if (!isLoading && !store) {
      return <Redirect to="/not-found" />;
    }

    if (!store) return null;

    const { _id: store_id, ...activeStore } = store;

    const whileQueuedMessage = activeStore?.queueing?.onQueueMessage;
    const postQueueMessage = activeStore?.queueing?.postQueueMessage;
    const maxWaitTime = activeStore?.queueing?.dequeuedWaitTimeLimit || 0;

    return (
      <>
        <AnimationStyles />
        <TitleHelmet
          title={view === "form" ? "Walk-in Queue" : "Your Queue Position"}
          showWebsiteName={false}
        />
        {!isLoading && (
          <PageCenter loginstyle={{ background: "#f8f9fa" }}>
            {view === "validating_qr" && (
              <div style={{ textAlign: "center", padding: "3rem" }}>
                <Spinner animation="border" variant="primary" />
              </div>
            )}
            {view === "invalid_qr" && (
              <div style={minimalCardStyle}>
                <div style={{ textAlign: "center" }}>
                  {data.stores.length && (
                    <img
                      src={data.stores[0].partner_logo}
                      alt="Brand logo"
                      style={logoStyle}
                    />
                  )}
                  <h5 style={{ marginBottom: "0.5rem", color: "#dc3545" }}>
                    QR Code Expired
                  </h5>
                  <p style={{ color: "#6c757d", marginBottom: 0 }}>
                    This QR code has expired. Please ask a staff member for
                    today&apos;s QR code.
                  </p>
                </div>
              </div>
            )}
            {view === "form" && (
              <WalkInForm
                brandLogo={
                  data.stores.length
                    ? data.stores[0].partner_logo
                    : data.brand_logo
                }
                storeId={store_id}
                onSuccess={this.handleSuccessfulRegistration}
                api={this.api}
                queueSettings={
                  activeStore.queueing || {
                    showReservationExperiences: true,
                    customerPreferenceToggles: {
                      specialOccasion: {
                        enable: true,
                        customList: [],
                        customHeader: "",
                        allowUserInput: false,
                        isRequired: false,
                      },
                      dietary: {
                        enable: true,
                        customList: [],
                        customHeader: "",
                        allowUserInput: false,
                        isRequired: false,
                      },
                      seating: {
                        enable: false,
                        customList: [],
                        customHeader: "",
                        allowUserInput: false,
                        isRequired: false,
                      },
                    },
                  }
                }
              />
            )}
            {view === "position" && (
              <QueuePositionDisplay
                brandLogo={
                  data.stores.length
                    ? data.stores[0].partner_logo
                    : data.brand_logo
                }
                storeId={store_id}
                queueData={
                  queueData || {
                    listKey: this.state.listKey,
                    redisKey: this.state.redisKey,
                  }
                }
                api={this.api}
                onCancelSuccess={this.handleCancelSuccess}
                whileQueuedMessage={whileQueuedMessage}
                nextInLineMessage={postQueueMessage}
                onNextMaxWaitTime={maxWaitTime}
              />
            )}
          </PageCenter>
        )}
      </>
    );
  }
}

const MySwal = withReactContent(Swal.mixin({ scrollbarPadding: false }));

const ALLERGY_OPTIONS = [
  "Peanuts",
  "Lactose Intolerant",
  "Mushrooms",
  "Shellfish",
  "Soy",
  "Eggs",
  "Wheat",
];

const PREFERENCE_OPTIONS = [
  "Diabetic",
  "Vegetarian",
  "Pescatarian",
  "Kosher",
  "Pregnant",
];

const CELEBRATION_OPTIONS = [
  "Birthday",
  "Anniversary",
  "Graduation",
  "Engagement",
  "Reunion",
  "Promotion",
  "Baby Shower",
  "Bridal Shower",
  "Wedding",
];

const SEATING_OPTIONS = ["Outdoor", "Indoor", "Private Room", "Bar Area"];

const ToggleSwitch = ({ isOn, handleToggle }) => {
  const purpleColor = "#8950fc";

  const switchContainerStyle = {
    position: "relative",
    display: "inline-block",
    width: "50px",
    height: "28px",
    borderRadius: "17px",
    backgroundColor: isOn ? purpleColor : "#e0e0e0",
    transition: "background-color 0.2s ease",
    cursor: "pointer",
    verticalAlign: "middle",
    marginRight: "10px",
  };

  const switchCircleStyle = {
    position: "absolute",
    top: "2px",
    left: isOn ? "calc(100% - 26px)" : "2px",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    transition: "left 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const checkmarkStyle = {
    color: purpleColor,
    fontSize: "18px",
    fontWeight: "bold",
    opacity: isOn ? 1 : 0,
    transition: "opacity 0.2s ease",
  };

  return (
    <div style={switchContainerStyle} onClick={handleToggle}>
      <div style={switchCircleStyle}>
        {isOn && <span style={checkmarkStyle}>✓</span>}
      </div>
    </div>
  );
};

// Fields whose *format* is checked (on blur / submit), on top of "required".
const FORMAT_CHECKED_FIELDS = ["mobileNumber", "email"];
const FORMAT_ERRORS = [MOBILE_ERROR, EMAIL_ERROR];

export class WalkInForm extends ProSourceForm {
  constructor(props) {
    super(props);
    this.state = {
      values: {
        firstName: "",
        lastName: "",
        email: "",
        mobileNumber: "",
        partySize: "",
        allergies: [],
        dietaryPreferences: [],
        otherNotes: "",
        customDietaryItems: [],
        celebration: [],
        seating: [],
        otherCelebrationNote: "", // <-- ADDED
        preferredExperience: "",
      },
      errors: {},
      touched: {},
      // fields whose format errors are visible: set on blur and on submit, so
      // a guest isn't told "invalid" while still typing a number
      checked: {},
      isSubmitting: false,
      experiences: [],

      showDietary: false,
      showCelebration: false,
      showExperience: false,
    };
    this.validate = {
      required: ["firstName", "lastName", "mobileNumber", "partySize", "email"],
    };
  }

  async componentDidMount() {
    this._isMounted = true;
    const { storeId, api } = this.props;

    if (storeId && api) {
      try {
        const { data } = await api.get(
          `/branded-website/reservation/${storeId}/service/current`,
        );
        if (this._isMounted && Array.isArray(data)) {
          const allExperiences = data.flatMap(
            (service) => service.experiences || [],
          );
          const uniqueExperiences = allExperiences.reduce((acc, current) => {
            if (!acc.find((item) => item._id === current._id)) {
              acc.push(current);
            }
            return acc;
          }, []);
          this.setState({ experiences: uniqueExperiences });
        }
      } catch (error) {
        console.error("Failed to fetch experiences:", error);
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  setAllFieldsToTouched = async () => {
    const touched = {};
    this.getFields().forEach((field) => {
      touched[field] = true;
    });
    const checked = {};
    FORMAT_CHECKED_FIELDS.forEach((field) => {
      checked[field] = true;
    });
    this.setState({ touched, checked });
  };

  // Required-field errors (base class) plus mobile/email format errors.
  validateForm = async () => {
    const errors = this.handleFormValidation();
    const formatErrors = validateWalkInDetails(this.state.values);

    FORMAT_CHECKED_FIELDS.forEach((field) => {
      if (this.state.checked[field] && formatErrors[field]) {
        errors[field] = formatErrors[field];
      } else if (FORMAT_ERRORS.includes(errors[field])) {
        delete errors[field];
      }
    });

    this.setState({ errors });
    return errors;
  };

  // ProSourceForm calls these hooks after its own blur handling.
  onBlur = (event) => {
    const { name } = event.target;
    if (!FORMAT_CHECKED_FIELDS.includes(name) || this.state.checked[name]) {
      return;
    }
    this.setState(
      (prev) => ({ checked: { ...prev.checked, [name]: true } }),
      this.validateForm,
    );
  };

  toggleDietary = () => {
    this.setState((prevState) => ({ showDietary: !prevState.showDietary }));
  };

  toggleCelebration = () => {
    this.setState((prevState) => ({
      showCelebration: !prevState.showCelebration,
    }));
  };

  toggleSeating = (seatingOptions) => () => {
    this.setState((prevState) => ({
      showSeating: !prevState.showSeating,
      values: {
        ...prevState.values,
        seating: prevState.showSeating
          ? []
          : seatingOptions.length === 1
          ? [seatingOptions[0]]
          : [],
      },
    }));
  };

  toggleExperience = () => {
    this.setState((prevState) => ({
      showExperience: !prevState.showExperience,
    }));
  };

  handlePillClick = (key, value) => {
    const newValue = this.state.values[key] === value ? "" : value;
    this.handleOnChange({ target: { name: key, value: newValue } });
  };

  handleMultiPillClick = (key, value) => {
    const currentValues = [...this.state.values[key]];
    const index = currentValues.indexOf(value);

    if (index > -1) {
      currentValues.splice(index, 1);
    } else {
      if (
        (key === "celebration" || key === "seating") &&
        currentValues.length >= 1
      ) {
        console.warn(`Maximum of 1 ${key} types allowed.`);
        return;
      }
      currentValues.push(value);
    }

    this.handleOnChange({ target: { name: key, value: currentValues } });
  };

  handleAddCustomItem = () => {
    const { values } = this.state;
    const itemName = values.otherNotes.trim();

    if (!itemName) {
      return;
    }

    const itemExists =
      values.allergies.some(
        (item) => item.name.toLowerCase() === itemName.toLowerCase(),
      ) ||
      values.dietaryPreferences.some(
        (item) => item.name.toLowerCase() === itemName.toLowerCase(),
      ) ||
      values.customDietaryItems.some(
        (item) => item.name.toLowerCase() === itemName.toLowerCase(),
      );

    if (itemExists) {
      console.warn("Item already added");
      this.handleOnChange({ target: { name: "otherNotes", value: "" } });
      return;
    }

    const newItem = { name: itemName, count: 1 };
    this.handleOnChange({
      target: {
        name: "customDietaryItems",
        value: [...values.customDietaryItems, newItem],
      },
    });

    this.handleOnChange({ target: { name: "otherNotes", value: "" } });
  };

  // --- NEW: Handler for custom celebration ---
  handleAddCustomCelebration = () => {
    const { values } = this.state;
    const itemName = values.otherCelebrationNote.trim();

    if (!itemName) {
      return;
    }

    if (values.celebration.length >= 1) {
      console.warn("Maximum of 1 celebration types allowed.");
      return;
    }

    const itemExists = values.celebration.some(
      (item) => item.toLowerCase() === itemName.toLowerCase(),
    );

    if (itemExists) {
      console.warn("Celebration already added");
      this.handleOnChange({
        target: { name: "otherCelebrationNote", value: "" },
      });
      return;
    }

    this.handleOnChange({
      target: {
        name: "celebration",
        value: [...values.celebration, itemName],
      },
    });

    this.handleOnChange({
      target: { name: "otherCelebrationNote", value: "" },
    });
  };
  // --- END NEW ---

  handleSelectDietaryItem = (key, itemName) => {
    const { values } = this.state;
    const currentItems = [...values[key]];
    const existingItemIndex = currentItems.findIndex(
      (item) => item.name === itemName,
    );
    let newItems;

    if (existingItemIndex > -1) {
      // Item exists, so we are *removing* it. This is always allowed.
      newItems = currentItems.filter((item) => item.name !== itemName);
    } else {
      // Item doesn't exist, we are *adding* it (count 1).
      if (key === "allergies" && values.allergies.length >= 2) {
        console.warn("Maximum of 2 allergy types allowed.");
        return;
      }
      if (
        key === "dietaryPreferences" &&
        values.dietaryPreferences.length >= 1
      ) {
        console.warn("Maximum of 1 preference type allowed.");
        return;
      }

      newItems = [...currentItems, { name: itemName, count: 1 }];
    }

    this.handleOnChange({ target: { name: key, value: newItems } });
  };

  handleUpdateDietaryCount = (key, itemName, newCount) => {
    const { values } = this.state;
    const currentItems = [...values[key]];
    const itemToUpdate = currentItems.find((item) => item.name === itemName);

    if (!itemToUpdate) return; // Should not happen

    const partySize = parseInt(values.partySize, 10) || 0;

    // Validate that the new count does not exceed party size
    if (newCount > partySize) {
      console.warn(
        `Count for '${itemName}' cannot exceed party size of ${partySize}.`,
      );
      return;
    }

    // Proceed with the update (either increment or decrement)
    let newItems;
    if (newCount < 1) {
      // Decrement resulted in removal
      newItems = currentItems.filter((item) => item.name !== itemName);
    } else {
      // Update count (for either +1 or -1)
      newItems = currentItems.map((item) =>
        item.name === itemName ? { ...item, count: newCount } : item,
      );
    }

    this.handleOnChange({ target: { name: key, value: newItems } });
  };

  onSubmit = async (values, setSubmitting) => {
    const { storeId, api, onSuccess } = this.props;
    const purpleColor = "#8950fc";

    // Read the details back before joining (and before asking for
    // notification permission): a wrong digit means the host can't reach them.
    // Not "joining" yet — the guest is still reviewing.
    setSubmitting(false);
    if (!(await confirmWalkInDetails(values, MySwal))) {
      return;
    }
    setSubmitting(true);

    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    const { experiences } = this.state;
    const experienceId = get(values, "preferredExperience", "");
    const selectedExperience = experiences.find(
      (exp) => exp._id === experienceId,
    );
    const experienceName = selectedExperience ? selectedExperience.name : "";

    try {
      // --- NEW: Combine all dietary items ---
      const allDietaryItems = [
        ...get(values, "allergies", []),
        ...get(values, "dietaryPreferences", []),
        ...get(values, "customDietaryItems", []),
      ];

      // Add any leftover text from the 'otherNotes' box
      const leftoverNotes = get(values, "otherNotes", "").trim();
      if (leftoverNotes) {
        allDietaryItems.push({ name: leftoverNotes, count: 1 });
      }

      // Format for the 'notes' payload (using 'label' as requested)
      const finalDietaryNotes = allDietaryItems.map((item) => ({
        label: item.name,
        count: item.count,
      }));

      // Get celebration data
      const celebrationNotes = get(values, "celebration", []).map((item) => ({
        label: item,
      }));

      const seatingNotes = get(values, "seating", []).map((item) => ({
        label: item,
      }));

      // Create the 'notes' object
      const notesPayload = {
        dietary: finalDietaryNotes,
        celebration: celebrationNotes,
        seatingAreaPreference: seatingNotes,
      };
      // --- END NEW LOGIC ---

      const payload = {
        firstName: get(values, "firstName", ""),
        lastName: get(values, "lastName", ""),
        email: get(values, "email", ""),
        mobileNumber: get(values, "mobileNumber", ""),
        partySize: parseInt(get(values, "partySize", 0), 10),

        // --- MODIFIED: Replaced old keys with new 'notes' object ---
        notes: notesPayload,
        // ---
        experienceId,
        experience: experienceName,
      };

      const { data } = await api.post({
        url: `/branded-website/reservation/${storeId}/walk-in`,
        data: payload,
      });

      if (data.success) {
        if (onSuccess) {
          onSuccess({
            listKey: data.listKey,
            redisKey: data.redisKey,
            cancelToken: data.cancelToken,
          });
        }
        console.log("Submission successful:", data);
      } else {
        MySwal.fire({
          icon: "error",
          title: "Error",
          text: data.error_message || "Something went wrong.",
          confirmButtonColor: purpleColor,
        });
      }
    } catch (error) {
      console.log(error);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.error_message ||
          "An error occurred. Please try again.",
        confirmButtonColor: purpleColor,
      });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };
  render() {
    const {
      brandLogo = "https://via.placeholder.com/150x50.png?text=Logo",
      queueSettings,
    } = this.props;
    const {
      values,
      isSubmitting,
      experiences,
      showDietary,
      showCelebration,
      showSeating,
    } = this.state;

    const purpleColor = "#8950fc";

    const actions = {
      handleFeedbackError: this.handleFeedbackError,
      isTouched: this.isTouched,
    };
    const inputActions = {
      onChange: this.handleOnChange,
      onBlur: this.handleOnBlur,
    };

    const toggleLabelStyle = {
      display: "flex",
      alignItems: "center",
      marginBottom: "1rem",
      fontWeight: "bold",
      justifyContent: "space-between",
    };

    const basePillStyle = {
      margin: "0.25rem",
      padding: "0.5rem 0.75rem",
      cursor: "pointer",
      fontSize: "0.9rem",
      fontWeight: "500",
      transition: "all 0.2s ease",
      border: "none",
      borderRadius: "50rem",
    };

    const experiencePillStyle = {
      ...basePillStyle,
      backgroundColor: "#f0f0f0",
      color: "#555",
    };

    const activeExperiencePillStyle = {
      ...basePillStyle,
      backgroundColor: purpleColor,
      color: "#ffffff",
    };

    const selectedAllergies = values.allergies.map((item) => ({
      ...item,
      key: "allergies",
    }));
    const selectedPrefs = values.dietaryPreferences.map((item) => ({
      ...item,
      key: "dietaryPreferences",
    }));
    const selectedCustom = values.customDietaryItems.map((item) => ({
      ...item,
      key: "customDietaryItems",
    }));
    const selectedItems = [
      ...selectedAllergies,
      ...selectedPrefs,
      ...selectedCustom,
    ];

    const celebrationToggle =
      queueSettings?.customerPreferenceToggles.specialOccasion;
    const dietaryToggle = queueSettings?.customerPreferenceToggles.dietary;
    const seatingToggle = queueSettings?.customerPreferenceToggles.seating;

    const seatingOptions =
      Array.isArray(seatingToggle?.customList) &&
      seatingToggle?.customList.length
        ? seatingToggle.customList
        : SEATING_OPTIONS;

    return (
      <div style={minimalCardStyle} className="card-animate-enter">
        <div className="text-center mb-4">
          <img src={brandLogo} alt="Store Logo" style={logoStyle} />
          <h3 className="font-weight-bold text-dark">Join the Walk-in Queue</h3>
        </div>

        <Form onSubmit={this.handleOnSubmit}>
          <Row>
            <Col>
              <FormItem
                label="First Name"
                name="firstName"
                inputProps={{
                  name: "firstName",
                  value: values.firstName,
                  style: { backgroundColor: "#f8f9fa" },
                }}
                inputActions={inputActions}
                actions={actions}
              />
            </Col>
            <Col>
              <FormItem
                label="Last Name"
                name="lastName"
                inputProps={{
                  name: "lastName",
                  value: values.lastName,
                  style: { backgroundColor: "#f8f9fa" },
                }}
                inputActions={inputActions}
                actions={actions}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FormItem
                label="Email"
                name="email"
                type="email"
                inputProps={{
                  name: "email",
                  value: values.email,
                  style: { backgroundColor: "#f8f9fa" },
                }}
                inputActions={inputActions}
                actions={actions}
              />
            </Col>
            <Col>
              <FormItem
                label="Mobile Number"
                name="mobileNumber"
                placeholder="0917 123 4567"
                inputProps={{
                  name: "mobileNumber",
                  value: values.mobileNumber,
                  type: "tel",
                  style: { backgroundColor: "#f8f9fa" },
                }}
                inputActions={inputActions}
                actions={actions}
              />
            </Col>
          </Row>
          <FormItem
            label="Party Size"
            name="partySize"
            type="number"
            inputProps={{
              name: "partySize",
              value: values.partySize || "",
              min: 1,
              style: { backgroundColor: "#f8f9fa" },
            }}
            inputActions={inputActions}
            actions={actions}
          />

          <hr />

          {dietaryToggle?.enable && (
            <>
              <div style={toggleLabelStyle}>
                Any dietary preferences or restrictions?
                <ToggleSwitch
                  isOn={showDietary}
                  handleToggle={this.toggleDietary}
                />
              </div>

              {showDietary && (
                <div className="mb-3 card-animate-enter">
                  <Form.Group
                    className="mb-3 p-3 border rounded"
                    style={{ backgroundColor: "#f8f9fa" }}
                  >
                    <Form.Label className="font-weight-bold">
                      Dietary Preference
                    </Form.Label>
                    <div className="mb-2">
                      <Form.Label as="small" className="text-muted">
                        Allergies
                      </Form.Label>
                      <div>
                        {ALLERGY_OPTIONS.map((option) => (
                          <Badge
                            key={option}
                            pill
                            style={
                              values.allergies.some(
                                (item) => item.name === option,
                              )
                                ? activeExperiencePillStyle
                                : experiencePillStyle
                            }
                            onClick={() =>
                              this.handleSelectDietaryItem("allergies", option)
                            }
                          >
                            {option}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Form.Label as="small" className="text-muted">
                        Preferences
                      </Form.Label>
                      <div>
                        {PREFERENCE_OPTIONS.map((option) => (
                          <Badge
                            key={option}
                            pill
                            style={
                              values.dietaryPreferences.some(
                                (item) => item.name === option,
                              )
                                ? activeExperiencePillStyle
                                : experiencePillStyle
                            }
                            onClick={() =>
                              this.handleSelectDietaryItem(
                                "dietaryPreferences",
                                option,
                              )
                            }
                          >
                            {option}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="otherNotesInput">
                      Other Dietary Notes
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        id="otherNotesInput"
                        name="otherNotes"
                        value={values.otherNotes}
                        style={{ backgroundColor: "#f8f9fa" }}
                        placeholder="e.g., Tomato"
                        onChange={inputActions.onChange}
                        onBlur={inputActions.onBlur}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            this.handleAddCustomItem();
                          }
                        }}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={this.handleAddCustomItem}
                      >
                        Add
                      </Button>
                    </InputGroup>
                  </Form.Group>

                  {selectedItems.length > 0 && (
                    <div
                      className="mb-3 p-3 border rounded"
                      style={{ backgroundColor: "#ffffff" }}
                    >
                      {selectedItems.map((item, index) => (
                        <div
                          key={item.name}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "0.5rem 0",
                            borderBottom:
                              index === selectedItems.length - 1
                                ? "none"
                                : "1px solid #eee",
                          }}
                        >
                          <span style={{ fontWeight: "500" }}>{item.name}</span>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Button
                              variant="light"
                              size="sm"
                              style={{
                                padding: "0.25rem 0.6rem",
                                margin: "0 0.25rem",
                                lineHeight: 1,
                              }}
                              onClick={() =>
                                this.handleUpdateDietaryCount(
                                  item.key,
                                  item.name,
                                  item.count - 1,
                                )
                              }
                            >
                              -
                            </Button>
                            <span
                              style={{
                                minWidth: "20px",
                                textAlign: "center",
                                fontWeight: "bold",
                                margin: "0 0.25rem",
                              }}
                            >
                              {item.count}
                            </span>
                            <Button
                              variant="light"
                              size="sm"
                              style={{
                                padding: "0.25rem 0.6rem",
                                margin: "0 0.25rem",
                                lineHeight: 1,
                              }}
                              onClick={() =>
                                this.handleUpdateDietaryCount(
                                  item.key,
                                  item.name,
                                  item.count + 1,
                                )
                              }
                            >
                              +
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {celebrationToggle?.enable && (
            <>
              <div style={toggleLabelStyle}>
                Are you celebrating a special occasion?{" "}
                <ToggleSwitch
                  isOn={showCelebration}
                  handleToggle={this.toggleCelebration}
                />
              </div>

              {showCelebration && (
                <div className="mb-3 card-animate-enter">
                  <Form.Group>
                    <Form.Label>Celebration</Form.Label>
                    <div>
                      {CELEBRATION_OPTIONS.map((option) => (
                        <Badge
                          key={option}
                          pill
                          style={
                            values.celebration.includes(option)
                              ? activeExperiencePillStyle
                              : experiencePillStyle
                          }
                          onClick={() =>
                            this.handleMultiPillClick("celebration", option)
                          }
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="otherCelebrationNoteInput">
                      Other Occasion
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        id="otherCelebrationNoteInput"
                        name="otherCelebrationNote"
                        value={values.otherCelebrationNote}
                        style={{ backgroundColor: "#f8f9fa" }}
                        placeholder="e.g., Family Reunion"
                        onChange={inputActions.onChange}
                        onBlur={inputActions.onBlur}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            this.handleAddCustomCelebration();
                          }
                        }}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={this.handleAddCustomCelebration}
                      >
                        Add
                      </Button>
                    </InputGroup>
                  </Form.Group>

                  {values.celebration.length > 0 && (
                    <div
                      className="mb-3 p-3 border rounded"
                      style={{ backgroundColor: "#ffffff" }}
                    >
                      {values.celebration.map((item, index) => (
                        <div
                          key={item}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "0.5rem 0",
                            borderBottom:
                              index === values.celebration.length - 1
                                ? "none"
                                : "1px solid #eee",
                          }}
                        >
                          <span style={{ fontWeight: "500" }}>{item}</span>
                          <Button
                            variant="light"
                            size="sm"
                            style={{
                              padding: "0.25rem 0.6rem",
                              margin: "0 0.25rem",
                              lineHeight: 1,
                            }}
                            onClick={() =>
                              this.handleMultiPillClick("celebration", item)
                            }
                          >
                            &times;
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {seatingToggle?.enable && (
            <>
              <div style={toggleLabelStyle}>
                {seatingToggle?.customHeader || "Where would you like to sit?"}{" "}
                <ToggleSwitch
                  isOn={showSeating}
                  handleToggle={this.toggleSeating(seatingOptions)}
                />
              </div>

              {showSeating && seatingOptions.length > 1 && (
                <div className="mb-3 card-animate-enter">
                  <Form.Group>
                    <Form.Label>Seating</Form.Label>
                    <div>
                      {seatingOptions.map((option) => (
                        <Badge
                          key={option}
                          pill
                          style={
                            values.seating.includes(option)
                              ? activeExperiencePillStyle
                              : experiencePillStyle
                          }
                          onClick={() =>
                            this.handleMultiPillClick("seating", option)
                          }
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </Form.Group>
                </div>
              )}
            </>
          )}

          {queueSettings.showReservationExperience &&
            experiences.length > 0 && (
              <>
                <div style={toggleLabelStyle}>
                  Choose your preferred experience:
                </div>

                <Form.Group className="mb-3 card-animate-enter">
                  <div>
                    {experiences.map((exp) => (
                      <Badge
                        key={exp._id}
                        pill
                        style={
                          values.preferredExperience === exp._id
                            ? activeExperiencePillStyle
                            : experiencePillStyle
                        }
                        onClick={() =>
                          this.handlePillClick("preferredExperience", exp._id)
                        }
                      >
                        {exp.name}
                      </Badge>
                    ))}
                  </div>
                </Form.Group>
              </>
            )}

          <Button
            type="submit"
            variant="primary"
            className="w-100 mt-4 animated-queue-button"
            disabled={isSubmitting}
            style={{
              backgroundColor: purpleColor,
              borderColor: purpleColor,
              fontWeight: "bold",
            }}
          >
            {isSubmitting ? "Joining..." : "Join Queue"}
          </Button>
        </Form>
      </div>
    );
  }
}

export function QueuePositionDisplay({
  brandLogo,
  storeId,
  queueData,
  api,
  onCancelSuccess,
  whileQueuedMessage,
  nextInLineMessage,
  onNextMaxWaitTime = 0,
}) {
  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(0);
  const [fetchError, setFetchError] = useState(false);
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== "undefined" && typeof navigator.onLine === "boolean"
      ? !navigator.onLine
      : false,
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (document.body.style.paddingRight) {
        document.body.style.paddingRight = "0px";
      }
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });
    return () => observer.disconnect();
  }, []);

  const fetchPosition = useCallback(
    async (showLoading = true) => {
      if (!queueData?.listKey || !queueData?.redisKey) return;
      if (showLoading) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      try {
        const { data } = await api.get(
          `/branded-website/reservation/queue?store_id=${storeId}&redisKey=${queueData.redisKey}`,
        );
        if (data && typeof data.index === "number") {
          setIndex(data.index);
          setFetchError(false);
          setIsOffline(false);
        } else {
          setFetchError(true);
        }
      } catch (error) {
        console.error("Failed to fetch queue position:", error);
        setFetchError(true);
        if (typeof navigator !== "undefined" && !navigator.onLine) {
          setIsOffline(true);
        }
        // Safeguard: Do NOT wipe index to null if we already have a valid position
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [api, queueData, storeId],
  );

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      fetchPosition(false);
    };
    const handleOffline = () => {
      setIsOffline(true);
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [fetchPosition]);

  useEffect(() => {
    fetchPosition();
    const interval = setInterval(() => fetchPosition(false), 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchPosition]);

  useEffect(() => {
    if (index === -1) {
      removeStorage("walkInQueue");
      localStorage.removeItem(`queueCountdownEndTime_${queueData?.redisKey}`);
    }
  }, [index, queueData?.redisKey]);

  useEffect(() => {
    if (index === 0 && onNextMaxWaitTime > 0) {
      const storageKey = `queueCountdownEndTime_${queueData?.redisKey}`;
      const stored = localStorage.getItem(storageKey);
      const storedEndTime = stored ? parseInt(stored, 10) : null;
      const endTime =
        storedEndTime && storedEndTime > Date.now()
          ? storedEndTime
          : Date.now() + onNextMaxWaitTime * 1000;

      if (!storedEndTime || storedEndTime <= Date.now()) {
        localStorage.setItem(storageKey, String(endTime));
      }

      setCountdown(Math.round((endTime - Date.now()) / 1000));

      const timer = setInterval(() => {
        const remaining = Math.round((endTime - Date.now()) / 1000);
        if (remaining <= 0) {
          clearInterval(timer);
          setCountdown(0);
          localStorage.removeItem(storageKey);
          silentCancel();
        } else {
          setCountdown(remaining);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [index, onNextMaxWaitTime, queueData?.redisKey]);

  useEffect(() => {
    if (index !== 0) return;

    const originalTitle = document.title;

    // SweetAlert for when the tab is active
    MySwal.fire({
      icon: "info",
      title: "You're Next!",
      text: "Please standby, you're next in line!",
      confirmButtonColor: "#8950fc",
      timer: 10000,
      timerProgressBar: true,
    });

    // Document title flash — visible in iOS/browser tab & app switcher
    document.title = "\uD83D\uDD14 You're Next!";
    const titleTimeout = setTimeout(() => {
      document.title = originalTitle;
    }, 15000);

    // Audio alert — chime sequence, unlocked by the prior form-submit gesture, works on iOS
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        const ctx = new AudioCtx();

        // Chime notes: C5 → E5 → G5 → C6 (ascending chord)
        const chimes = [
          { freq: 523.25, start: 0.0, duration: 1.2 },
          { freq: 659.25, start: 0.25, duration: 1.2 },
          { freq: 783.99, start: 0.5, duration: 1.2 },
          { freq: 1046.5, start: 0.75, duration: 1.6 },
        ];

        chimes.forEach(({ freq, start, duration }) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
          gain.gain.setValueAtTime(0, ctx.currentTime + start);
          gain.gain.linearRampToValueAtTime(
            0.25,
            ctx.currentTime + start + 0.02,
          );
          gain.gain.exponentialRampToValueAtTime(
            0.0001,
            ctx.currentTime + start + duration,
          );
          osc.start(ctx.currentTime + start);
          osc.stop(ctx.currentTime + start + duration);
        });
      }
    } catch (_) {
      // Audio not available — silently ignored
    }

    // OS-level browser notification for desktop / Android Chrome
    let notification = null;
    if ("Notification" in window && Notification.permission === "granted") {
      try {
        notification = new Notification("You're Next!", {
          body: "Please standby, you're next in line!",
          icon: brandLogo,
        });
        notification.onclick = () => window.focus();
        setTimeout(() => notification && notification.close(), 10000);
      } catch (_) {
        // Notification creation failed — silently ignored
      }
    }

    return () => {
      clearTimeout(titleTimeout);
      document.title = originalTitle;
      if (notification) notification.close();
    };
  }, [index, brandLogo]);

  const silentCancel = async () => {
    try {
      await api.post({
        url: `/branded-website/reservation/${storeId}/walk-in/cancel`,
        data: {
          listKey: queueData.listKey,
          redisKey: queueData.redisKey,
          cancelToken: queueData.cancelToken,
          expiredTimer: true, // TODO: rename this, always record cancelled reservation
        },
      });
      localStorage.removeItem(`queueCountdownEndTime_${queueData?.redisKey}`);
      onCancelSuccess();
    } catch (error) {
      console.error("Failed to cancel reservation:", error);
    }
  };

  const handleDetailsUpdated = () => {
    MySwal.fire({
      toast: true,
      position: "top",
      icon: "success",
      title: UPDATED_MESSAGE,
      showConfirmButton: false,
      timer: 3000,
    });
  };

  // The host already called, seated or removed this guest: say so, then
  // refresh the position so the screen reflects it.
  const handleEntryGone = () => {
    MySwal.fire({
      icon: "info",
      text: GONE_MESSAGE,
      confirmButtonColor: "#8950fc",
    });
    fetchPosition(true);
  };

  const handleCancel = async () => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: "This will remove you from the queue.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel it!",
      confirmButtonColor: "#dc3545",
      cancelButtonText: "No, keep my spot",
    });

    if (result.isConfirmed) {
      await silentCancel();
    }
  };

  const getOrdinalSuffix = (i) => {
    const j = i % 10,
      k = i % 100;
    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
  };

  const position = index !== null ? index + 1 : null;
  const contentKey =
    loading && index === null
      ? "loader"
      : fetchError && index === null
      ? "error"
      : position || (index === -1 ? "passed" : "unknown");

  return (
    <div
      style={{ ...minimalCardStyle, position: "relative" }}
      className="card-animate-enter"
    >
      <Button
        variant="link"
        onClick={() => fetchPosition(false)}
        disabled={loading || isRefreshing}
        style={{ position: "absolute", bottom: "30px", left: "20px" }}
        className="p-1"
      >
        <SVGIcon
          icon="Keens/reload"
          className={`svg-icon-primary ${
            loading || isRefreshing ? "spinner-border" : ""
          }`}
        />
      </Button>
      <div className="text-center mb-4">
        <img src={brandLogo} alt="Brand Logo" style={logoStyle} />
        <h3 className="font-weight-bold text-dark">Your Queue Position</h3>
      </div>

      <div className="text-center my-3" style={{ minHeight: "210px" }}>
        <div key={contentKey} className="content-pop-in">
          {loading && index === null ? (
            <>
              <Spinner animation="border" variant="primary" className="my-3" />
              <p className="text-muted">Loading your position...</p>
            </>
          ) : (fetchError || isOffline) && index === null ? (
            <>
              <div style={{ fontSize: "4rem" }}>📡</div>
              <h4 className="font-weight-bold mt-2 text-dark">
                Connection Error
              </h4>
              <p className="font-size-lg text-muted mb-3">
                Unable to retrieve your queue position. Please check your
                internet connection.
              </p>
              <Button
                variant="primary"
                className="mt-2 px-4 py-2"
                onClick={() => fetchPosition(true)}
                style={{ backgroundColor: "#8950fc", borderColor: "#8950fc" }}
              >
                Try Again
              </Button>
            </>
          ) : index === 0 ? (
            <>
              <div style={{ fontSize: "4rem" }}>🎉</div>
              <h4 className="text-info font-weight-bold mt-2">
                You’re next in line!
              </h4>
              {nextInLineMessage ? (
                <div className="mt-3 px-2">
                  {nextInLineMessage.title && (
                    <h5 className="font-weight-bold text-dark mb-1">
                      {nextInLineMessage.title}
                    </h5>
                  )}
                  {nextInLineMessage.body && (
                    <p className="text-dark mb-1">{nextInLineMessage.body}</p>
                  )}
                  {nextInLineMessage.footer && (
                    <small className="text-muted font-italic">
                      {nextInLineMessage.footer}
                    </small>
                  )}
                </div>
              ) : onNextMaxWaitTime > 0 ? (
                <div className="mt-3 px-2">
                  <p className="text-muted mb-1">
                    Your spot has been reserved. Please make your way to the
                    host within the allotted time to keep your place in line.
                  </p>
                </div>
              ) : null}
              {onNextMaxWaitTime > 0 && countdown > 0 && (
                <p className="text-dark mt-2">
                  Time remaining:{" "}
                  <strong>
                    {String(Math.floor(countdown / 60)).padStart(2, "0")}:
                    {String(countdown % 60).padStart(2, "0")}
                  </strong>
                </p>
              )}
              {(fetchError || isOffline) && (
                <div
                  className="alert alert-warning py-1 px-3 mt-3 mb-0 d-inline-flex align-items-center"
                  style={{ fontSize: "0.85rem", borderRadius: "20px" }}
                >
                  <span className="mr-2" role="img" aria-label="warning">
                    ⚠️
                  </span>
                  Offline: showing last updated position
                </div>
              )}
            </>
          ) : index > 0 ? (
            <>
              <div
                className="display-1 font-weight-bolder number-wiggle"
                style={{ fontSize: "8rem", lineHeight: 1, color: "#333" }}
              >
                {position}
              </div>

              {whileQueuedMessage ? (
                <div className="mt-3 px-2">
                  {whileQueuedMessage.title && (
                    <h5 className="font-weight-bold text-dark mb-1">
                      {whileQueuedMessage.title}
                    </h5>
                  )}
                  {whileQueuedMessage.body && (
                    <p className="text-dark mb-1">{whileQueuedMessage.body}</p>
                  )}
                  {whileQueuedMessage.footer && (
                    <small className="text-muted font-italic">
                      {whileQueuedMessage.footer}
                    </small>
                  )}
                </div>
              ) : (
                <p className="text-muted font-size-h4 mt-2 mb-2">
                  You are {position}
                  {getOrdinalSuffix(position)} on the line.
                </p>
              )}

              <div className="wave-container">
                <span className="wave-dot" style={{ animationDelay: "0s" }}>
                  .
                </span>
                <span className="wave-dot" style={{ animationDelay: "0.2s" }}>
                  .
                </span>
                <span className="wave-dot" style={{ animationDelay: "0.4s" }}>
                  .
                </span>
              </div>

              {(fetchError || isOffline) && (
                <div
                  className="alert alert-warning py-1 px-3 mt-3 mb-0 d-inline-flex align-items-center"
                  style={{ fontSize: "0.85rem", borderRadius: "20px" }}
                >
                  <span className="mr-2" role="img" aria-label="warning">
                    ⚠️
                  </span>
                  Offline: showing last updated position
                </div>
              )}
            </>
          ) : index === -1 ? (
            <>
              <div style={{ fontSize: "4rem" }}>🛎️</div>
              <h4 className="font-weight-bold mt-2">It's Your Turn!</h4>
              <p className="font-size-lg text-muted">
                Please approach the host for assistance.
              </p>
            </>
          ) : (
            <>
              <div style={{ fontSize: "4rem" }}>📡</div>
              <h4 className="font-weight-bold mt-2 text-dark">
                Unable to Load Queue Position
              </h4>
              <p className="font-size-lg text-muted mb-3">
                Please check your connection and try again.
              </p>
              <Button
                variant="primary"
                className="mt-2 px-4 py-2"
                onClick={() => fetchPosition(true)}
                style={{ backgroundColor: "#8950fc", borderColor: "#8950fc" }}
              >
                Try Again
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="text-right">
        {index === -1 ? (
          <Button
            variant="primary"
            className="p-2 cancel-button-animated"
            onClick={onCancelSuccess}
            style={{ backgroundColor: "#8950fc", borderColor: "#8950fc" }}
          >
            Queue another
          </Button>
        ) : (
          index !== null && (
            <>
              {queueData?.cancelToken && (
                <Button
                  variant="outline-primary"
                  className="p-2 mr-2"
                  onClick={() => setShowEdit(true)}
                >
                  Edit my details
                </Button>
              )}
              <Button
                variant="danger"
                className="text-danger p-2 cancel-button-animated text-white"
                onClick={handleCancel}
              >
                Cancel my reservation
              </Button>
            </>
          )
        )}
      </div>
      {queueData?.cancelToken && (
        <EditWalkInDetailsModal
          show={showEdit}
          onHide={() => setShowEdit(false)}
          api={api}
          storeId={storeId}
          queueData={queueData}
          onUpdated={handleDetailsUpdated}
          onGone={handleEntryGone}
        />
      )}
    </div>
  );
}
